import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { HelmetProvider } from "react-helmet-async";

// Critical path — loaded eagerly
import Home from "@/pages/Home";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

// Lazy-loaded public pages
const Contact = lazy(() => import("@/pages/Contact"));
const AuditResults = lazy(() => import("@/pages/AuditResults"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("@/pages/CheckoutCancel"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const CustomPage = lazy(() => import("@/pages/CustomPage"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));

// BPO Service Pages
const DataEntryPage = lazy(() => import("@/pages/services/DataEntry"));
const AccountsPayablePage = lazy(() => import("@/pages/services/AccountsPayable"));
const BackOfficePage = lazy(() => import("@/pages/services/BackOffice"));
const CustomerSupportPage = lazy(() => import("@/pages/services/CustomerSupport"));
const HROutsourcingPage = lazy(() => import("@/pages/services/HROutsourcing"));
const ITOutsourcingPage = lazy(() => import("@/pages/services/ITOutsourcing"));
const VirtualEmployeePage = lazy(() => import("@/pages/services/VirtualEmployee"));

// Digital Marketing Service Pages
const SEOServices = lazy(() => import("@/pages/SEOServices"));
const WebDevelopment = lazy(() => import("@/pages/WebDevelopment"));
const PPCAds = lazy(() => import("@/pages/PPCAds"));
const SocialMedia = lazy(() => import("@/pages/SocialMedia"));

// Money Pages (SEO landing pages)
const SEOAgencyLondon = lazy(() => import("@/pages/money/SEOAgencyLondon"));
const EcommerceSEO = lazy(() => import("@/pages/money/EcommerceSEO"));
const LocalSEOLondon = lazy(() => import("@/pages/money/LocalSEOLondon"));
const TechnicalSEO = lazy(() => import("@/pages/money/TechnicalSEO"));
const SEOConsultant = lazy(() => import("@/pages/money/SEOConsultant"));
const ShopifySEO = lazy(() => import("@/pages/money/ShopifySEO"));
const WordPressSEO = lazy(() => import("@/pages/money/WordPressSEO"));
const GoogleAdsAgency = lazy(() => import("@/pages/money/GoogleAdsAgency"));
const PPCConsultantPage = lazy(() => import("@/pages/money/PPCConsultant"));

// Lazy-loaded admin pages (heavy, rarely needed for visitors)
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminBlogs = lazy(() => import("@/pages/admin/Blogs"));
const AdminPackages = lazy(() => import("@/pages/admin/Packages"));
const AdminPages = lazy(() => import("@/pages/admin/Pages"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminChats = lazy(() => import("@/pages/admin/Chats"));
const AdminAudits = lazy(() => import("@/pages/admin/Audits"));

// Lazy-loaded non-critical components (chat, popups, tracking)
const ChatWidget = lazy(() => import("@/components/ChatWidget"));
const TrackingScripts = lazy(() => import("@/components/TrackingScripts"));
const WelcomePopup = lazy(() => import("@/components/WelcomePopup"));
const SalesNotification = lazy(() => import("@/components/SalesNotification"));
const ExitIntentPopup = lazy(() => import("@/components/ExitIntentPopup"));
const CodeInjection = lazy(() => import("@/components/CodeInjection"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#050505]">
    <div className="spinner"></div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user || user.role !== 'admin') return <Navigate to="/admin/login" replace />;
  return children;
};

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<><Navigation /><Home /><Footer /></>} />
        <Route path="/services/data-entry" element={<><Navigation /><DataEntryPage /><Footer /></>} />
        <Route path="/services/accounts-payable-receivable" element={<><Navigation /><AccountsPayablePage /><Footer /></>} />
        <Route path="/services/back-office-support" element={<><Navigation /><BackOfficePage /><Footer /></>} />
        <Route path="/services/customer-support" element={<><Navigation /><CustomerSupportPage /><Footer /></>} />
        <Route path="/services/hr-outsourcing" element={<><Navigation /><HROutsourcingPage /><Footer /></>} />
        <Route path="/services/it-outsourcing" element={<><Navigation /><ITOutsourcingPage /><Footer /></>} />
        <Route path="/services/virtual-employee" element={<><Navigation /><VirtualEmployeePage /><Footer /></>} />
        {/* Digital Marketing Services */}
        <Route path="/services/seo" element={<><Navigation /><SEOServices /><Footer /></>} />
        <Route path="/services/web-development" element={<><Navigation /><WebDevelopment /><Footer /></>} />
        <Route path="/services/ppc" element={<><Navigation /><PPCAds /><Footer /></>} />
        <Route path="/services/social-media" element={<><Navigation /><SocialMedia /><Footer /></>} />
        {/* Money Pages */}
        <Route path="/seo-agency-london" element={<><Navigation /><SEOAgencyLondon /><Footer /></>} />
        <Route path="/ecommerce-seo-services" element={<><Navigation /><EcommerceSEO /><Footer /></>} />
        <Route path="/local-seo-services-london" element={<><Navigation /><LocalSEOLondon /><Footer /></>} />
        <Route path="/technical-seo-services" element={<><Navigation /><TechnicalSEO /><Footer /></>} />
        <Route path="/seo-consultant-london" element={<><Navigation /><SEOConsultant /><Footer /></>} />
        <Route path="/shopify-seo-agency" element={<><Navigation /><ShopifySEO /><Footer /></>} />
        <Route path="/wordpress-seo-agency" element={<><Navigation /><WordPressSEO /><Footer /></>} />
        <Route path="/google-ads-agency" element={<><Navigation /><GoogleAdsAgency /><Footer /></>} />
        <Route path="/ppc-consultant" element={<><Navigation /><PPCConsultantPage /><Footer /></>} />
        <Route path="/contact" element={<><Navigation /><Contact /><Footer /></>} />
        <Route path="/audit-results" element={<><Navigation /><AuditResults /><Footer /></>} />
        <Route path="/checkout/success" element={<><Navigation /><CheckoutSuccess /><Footer /></>} />
        <Route path="/checkout/cancel" element={<><Navigation /><CheckoutCancel /><Footer /></>} />
        <Route path="/blog" element={<><Navigation /><Blog /><Footer /></>} />
        <Route path="/blog/:slug" element={<><Navigation /><BlogPost /><Footer /></>} />
        <Route path="/page/:slug" element={<><Navigation /><CustomPage /><Footer /></>} />
        
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/blogs" element={<ProtectedRoute><AdminBlogs /></ProtectedRoute>} />
        <Route path="/admin/packages" element={<ProtectedRoute><AdminPackages /></ProtectedRoute>} />
        <Route path="/admin/pages" element={<ProtectedRoute><AdminPages /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
        <Route path="/admin/chats" element={<ProtectedRoute><AdminChats /></ProtectedRoute>} />
        <Route path="/admin/audits" element={<ProtectedRoute><AdminAudits /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
};

const ChatWidgetWrapper = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  return (
    <Suspense fallback={null}>
      <ChatWidget />
    </Suspense>
  );
};

const PopupWrapper = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  return (
    <Suspense fallback={null}>
      <WelcomePopup />
      <SalesNotification />
      <ExitIntentPopup />
    </Suspense>
  );
};

const DeferredTrackingScripts = () => (
  <Suspense fallback={null}>
    <TrackingScripts />
  </Suspense>
);

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-[#F8FAFC]">
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <ChatWidgetWrapper />
            <PopupWrapper />
            <DeferredTrackingScripts />
            <Suspense fallback={null}><CodeInjection /></Suspense>
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
